import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import PriceUpdateModal from "@/components/PriceUpdateModal";
import { Plus, PencilSimple, Trash, Pill, ChartLine, Package as PackageIcon, ListChecks, Warning, Drop, TrendUp, CurrencyCircleDollar, ShoppingCart, Archive, ArrowCounterClockwise, UploadSimple, X, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { USAGE_UNITS, CATEGORIES, EMPTY, DOSE_UNITS_FOR, expiryBadge, EMPTY_BULL, EMPTY_PROTOCOL, muid } from "./constants";

export function MedProtocols() {
  const [meds, setMeds] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_PROTOCOL);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/medicines").then(r => setMeds(r.data)).catch(() => {});
    api.get("/medicines/protocols").then(r => setProtocols(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const medById = (id) => meds.find(m => m.id === id);

  const addRow = () => {
    const first = meds[0];
    setForm(f => ({ ...f, items: [...f.items, { _uid: muid(), medicine_id: first?.id || "", amount: 1, amount_unit: DOSE_UNITS_FOR(first?.usage_unit)[0] || "adet", days: f.default_duration_days || "", notes: "" }] }));
  };
  const updateRow = (i, patch) => setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const removeRow = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  // Varsayılan Süre (gün) değişince: elle değiştirilmemiş ilaç satırlarının Gün/Kez'ini senkronla
  const setDuration = (v) => setForm(f => ({ ...f, default_duration_days: v, items: f.items.map(it => it._daysManual ? it : { ...it, days: v }) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.items.length) { toast.error("En az bir ilaç ekleyin"); return; }
    const body = {
      name: form.name.trim(),
      disease: form.disease || null,
      default_duration_days: form.default_duration_days ? Number(form.default_duration_days) : null,
      notes: form.notes || null,
      items: form.items.map(it => ({ medicine_id: it.medicine_id, amount: Number(it.amount), amount_unit: it.amount_unit, days: it.days ? Number(it.days) : null, notes: it.notes || null })),
    };
    try {
      if (editId) { await api.patch(`/medicines/protocols/${editId}`, body); toast.success("Protokol güncellendi"); }
      else { await api.post("/medicines/protocols", body); toast.success("Protokol eklendi"); }
      setShowForm(false); setForm(EMPTY_PROTOCOL); setEditId(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };
  const edit = (p) => {
    setForm({
      name: p.name, disease: p.disease || "", default_duration_days: p.default_duration_days ?? "",
      notes: p.notes || "",
      items: (p.items || []).map(it => ({ _uid: muid(), medicine_id: it.medicine_id, amount: it.amount, amount_unit: it.amount_unit || DOSE_UNITS_FOR(it.stock_unit)[0], days: it.days ?? (p.default_duration_days ?? ""), _daysManual: it.days != null, notes: it.notes || "" })),
    });
    setEditId(p.id); setShowForm(true);
  };
  const del = async (id) => {
    if (!window.confirm("Protokol silinsin mi?")) return;
    try { await api.delete(`/medicines/protocols/${id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  return (
    <div data-testid="med-protocols">
      <div className="mb-6 rounded-xl border border-[#dfe7df] bg-[#EEF3E8] p-4 text-sm text-[#3d5142]">
        <div className="flex items-center gap-2 font-semibold text-[#2D4A36] mb-1"><ListChecks size={18} weight="duotone" /> Tedavi Protokolleri Nedir?</div>
        Sık karşılaşılan hastalıklar (Mastit, Metritis vb.) için birden fazla ilacı tek pakette tanımlayın. Veteriner modülünde bir tedavi
        açarken bu protokolü seçtiğinizde tüm ilaçlar tek tıkla eklenir ve her biri <b>kendi birimiyle</b> (ml, litre, gr, kg) stoktan otomatik düşülür.
      </div>

      <div className="flex justify-end mb-4">
        <Button data-testid="add-protocol-btn" onClick={() => { setForm(EMPTY_PROTOCOL); setEditId(null); setShowForm(true); if (!meds.length) toast.error("Önce ilaç ekleyin"); }}>
          <Plus size={16} className="inline mr-1" /> Yeni Protokol
        </Button>
      </div>

      {showForm && (
        <Card testId="protocol-form" className="mb-6">
          <h3 className="heading text-lg mb-4">{editId ? "Protokol Düzenle" : "Yeni Tedavi Protokolü"}</h3>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Protokol Adı *"><Input data-testid="protocol-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Örn: Mastit Standart" /></Field>
              <Field label="Hastalık"><Input data-testid="protocol-disease" value={form.disease} onChange={e => setForm({ ...form, disease: e.target.value })} placeholder="Örn: Mastit" /></Field>
              <Field label="Varsayılan Süre (gün)"><Input data-testid="protocol-duration" type="number" min="0" value={form.default_duration_days} onChange={e => setDuration(e.target.value)} /></Field>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-[#2D4A36]">İlaçlar</label>
                <Button type="button" variant="secondary" data-testid="protocol-add-row" onClick={addRow} disabled={!meds.length}>
                  <Plus size={14} className="inline mr-1" /> İlaç Ekle
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => {
                  const med = medById(it.medicine_id);
                  const doseUnits = DOSE_UNITS_FOR(med?.usage_unit);
                  return (
                    <div key={it._uid} data-testid={`protocol-item-${i}`} className="grid grid-cols-12 gap-2 items-end bg-[#F5F2EC] rounded-lg p-2">
                      <div className="col-span-4">
                        <Field label="İlaç">
                          <Select data-testid={`protocol-item-med-${i}`} value={it.medicine_id}
                                  onChange={e => { const m = medById(e.target.value); updateRow(i, { medicine_id: e.target.value, amount_unit: DOSE_UNITS_FOR(m?.usage_unit)[0] }); }}>
                            {meds.map(m => <option key={m.id} value={m.id}>{m.name} ({m.usage_unit})</option>)}
                          </Select>
                        </Field>
                      </div>
                      <div className="col-span-2">
                        <Field label="Doz"><Input data-testid={`protocol-item-amount-${i}`} type="number" step="0.0001" min="0" value={it.amount} onChange={e => updateRow(i, { amount: e.target.value })} /></Field>
                      </div>
                      <div className="col-span-2">
                        <Field label="Birim">
                          <Select data-testid={`protocol-item-unit-${i}`} value={it.amount_unit} onChange={e => updateRow(i, { amount_unit: e.target.value })}>
                            {doseUnits.map(u => <option key={u} value={u}>{u}</option>)}
                          </Select>
                        </Field>
                      </div>
                      <div className="col-span-3">
                        <Field label="Gün"><Input data-testid={`protocol-item-days-${i}`} type="number" min="1" step="1" value={it.days} onChange={e => updateRow(i, { days: e.target.value, _daysManual: true })} placeholder={form.default_duration_days || "1"} /></Field>
                      </div>
                      <div className="col-span-1 flex justify-center pb-1">
                        <button type="button" data-testid={`protocol-item-del-${i}`} onClick={() => removeRow(i)} className="p-2 rounded-md text-[#C45B45] hover:bg-[#FBECEA]" title="Kaldır"><Trash size={16} weight="bold" /></button>
                      </div>
                    </div>
                  );
                })}
                {!form.items.length && <div className="text-sm text-[#8a938c] py-2">Henüz ilaç eklenmedi. "İlaç Ekle" ile başlayın.</div>}
              </div>
            </div>

            <Field label="Notlar"><Textarea rows={1} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="secondary" onClick={() => { setShowForm(false); setForm(EMPTY_PROTOCOL); setEditId(null); }}>İptal</Button>
              <Button type="submit" data-testid="protocol-submit">{editId ? "Kaydet" : "Ekle"}</Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4" data-testid="protocol-list">
        {protocols.map(p => (
          <Card key={p.id} testId={`protocol-card-${p.id}`} className="!p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="heading text-base text-[#2D4A36] flex items-center gap-1.5"><ListChecks size={16} weight="duotone" /> {p.name}</div>
                {p.disease && <div className="text-xs text-[#5e675e] mt-0.5">Hastalık: {p.disease}{p.default_duration_days ? ` · ${p.default_duration_days} gün` : ""}</div>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => edit(p)} data-testid={`protocol-edit-${p.id}`} className="p-1.5 rounded-md bg-[#F5F2EC] text-[#1A1D1A] hover:bg-[#eae5db]" title="Düzenle"><PencilSimple size={14} weight="bold" /></button>
                <button onClick={() => del(p.id)} data-testid={`protocol-del-${p.id}`} className="p-1.5 rounded-md bg-[#FBECEA] text-[#C45B45] hover:bg-[#f5d5ce]" title="Sil"><Trash size={14} weight="bold" /></button>
              </div>
            </div>
            <ul className="text-sm space-y-1 mb-2">
              {(p.items || []).map((it, i) => (
                <li key={`${it.medicine_id}-${i}`} className={`flex items-center justify-between ${it.medicine_missing ? "text-[#C45B45]" : "text-[#3d4740]"}`}>
                  <span><Pill size={12} weight="duotone" className="inline mr-1 text-[#417251]" />{it.medicine_name}{it.medicine_missing ? " (silinmiş)" : ""}</span>
                  <span className="tabnum text-[#5e675e]">{Number(it.amount).toLocaleString("tr-TR")} {it.amount_unit}{it.days ? ` · ${it.days} gün` : ""}{it.line_cost != null ? ` · ${Number(it.line_cost).toLocaleString("tr-TR")} ₺` : ""}</span>
                </li>
              ))}
            </ul>
            {p.estimated_cost != null && <div className="text-sm font-semibold text-[#2D4A36] pt-2 border-t border-[#eee]">Tahmini Maliyet: {Number(p.estimated_cost).toLocaleString("tr-TR")} ₺</div>}
          </Card>
        ))}
        {!protocols.length && <div className="text-center py-10 text-[#8a938c] md:col-span-2">Henüz protokol yok. "Yeni Protokol" ile oluşturun.</div>}
      </div>
    </div>
  );
}
